import { useFormikContext } from "formik";
import { FC, useCallback, useState } from "react";
import { SwapDirection, SwapFormValues } from "@/components/DTOs/SwapFormValues";
import { Selector, SelectorContent, SelectorTrigger } from "@/components/Select/Selector/Index";
import { SelectedRouteDisplay } from "./Routes";
import useFormNetworks from "@/hooks/useFormNetworks";
import { Content } from "./Content";
import { Network, Token } from "@/Models/Network";
import clsx from "clsx";
import useWallet from "@/hooks/useWallet";
import useSuggestionsLimit from "@/hooks/useSuggestionsLimit";
import Balance from "@/components/Input/Amount/Balance";
import PickerWalletConnect from "./PickerWalletConnect";
import type { SwapQuote } from "@train-protocol/react";
import { captureEvent } from "@/lib/faro";

const RoutePicker: FC<{ direction: SwapDirection, className?: string, quote?: SwapQuote }> = ({ direction, className, quote }) => {
    const {
        values,
        setFieldValue,
    } = useFormikContext<SwapFormValues>();
    const [searchQuery, setSearchQuery] = useState("")
    const { wallets } = useWallet()

    const { suggestionsLimit } = useSuggestionsLimit({ hasWallet: wallets.length > 0 });

    const { isLoading, networkElements, selectedNetwork, selectedToken } = useFormNetworks({ direction, values }, searchQuery, suggestionsLimit)
    const currencyFieldName = direction === 'from' ? 'fromCurrency' : 'toCurrency';
    const handleSelect = useCallback(async (network: Network, token: Token) => {
        // Set the token
        await setFieldValue(currencyFieldName, token, true);
        // Set the network
        await setFieldValue(direction, network, true);
        captureEvent("route_selected", { direction, network: network.caip2Id, token: token.symbol });
    }, [currencyFieldName, direction, setFieldValue])

    return (
        <div className={clsx("flex flex-col self-end relative items-center", className)}>
            <Selector>
                <SelectorTrigger
                    onClick={() => captureEvent("route_picker_opened", { direction })}
                    disabled={false}
                    className="py-1.5 px-2 active:animate-press-down rounded-2xl bg-secondary-300"
                >
                    <SelectedRouteDisplay network={selectedNetwork} token={selectedToken} placeholder="Select token" />
                </SelectorTrigger>
                <SelectorContent
                    isLoading={isLoading}
                    searchHint="Search"
                    header={<PickerWalletConnect direction={direction} />}
                >
                    {({ closeModal }) => (
                        <Content
                            onSelect={(r, t) => { handleSelect(r, t); closeModal(); }}
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            rowElements={networkElements}
                            direction={direction}
                            selectedNetwork={selectedNetwork?.caip2Id}
                            selectedToken={selectedToken?.symbol}
                        />
                    )}
                </SelectorContent>
            </Selector>
            <Balance values={values} direction={direction} quote={quote} />
        </div>
    )
};

export default RoutePicker
